import { Button, Stack, StackProps, Text, useToast } from '@chakra-ui/react';
import { copyText } from '@pelicargo/ui';
import { earliestTenderOptions, timeOfDayOptions, TimeZone } from '@pelicargo/utils';
import { formatAddress } from '@pelicargo/utils';
import { ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import { HiOutlineClipboardCopy } from 'react-icons/hi';

import { trpc } from '../../config/trpc';
import { CachedRequest } from '../../hooks/request/useRequest';
import { Quote } from '../../utils/types';
import { ContactInput } from '../Autocomplete/ContactInput';
import { LabeledText } from '../LabeledText';
import { SimpleLabeledPopoverAddressInput } from '../SimpleLabeledPopoverAddressInput';
import { SimpleLabeledPopoverInput } from '../SimpleLabeledPopoverInput';

type Props = StackProps & {
  quote: Quote;
  request?: CachedRequest;
};

export const BookingSection = ({ title, children }: { title: string; children: ReactNode }) => {
  return (
    <Stack direction="column" spacing={2}>
      <Text fontSize="md" fontWeight="bold">
        {title}
      </Text>
      <Stack direction="column" spacing={0}>
        {children}
      </Stack>
    </Stack>
  );
};

export const AfrBookingForm = ({ quote, request, ...rest }: Props) => {
  const formContext = useFormContext();
  const deliveryAddress = formContext.watch('delivery_address');
  const originTimeZone = request?.origin_airports?.[0]?.time_zone;
  const toast = useToast();

  const { data: dropoff_contact } = trpc.getContact.useQuery({ id: quote?.dropoff_contact_id });

  const handleClick = async () => {
    const addressString = formatAddress(deliveryAddress);

    try {
      if (!addressString) throw new Error('No address to copy');

      copyText(addressString);
      toast({ title: 'Address copied!', status: 'success' });
    } catch (error) {
      toast({ title: 'Copy Failed', description: error?.message, status: 'error' });
    }
  };
  return (
    <Stack {...rest} spacing={6}>
      <Stack direction="row" flex={1} spacing={4}>
        <Stack flex={1} direction="column" spacing={6}>
          <BookingSection title="Booking Request Details">
            <SimpleLabeledPopoverInput isHorizontal name="awb_number" label="AWB #" />
            <SimpleLabeledPopoverInput
              name="tender_date"
              label="Tender Date"
              inputType="date-picker"
              initialTimeZone="Etc/UTC"
              showTime={false}
              isHorizontal
            />
            <SimpleLabeledPopoverInput
              name="tender_date_time_of_day"
              label="Time of Day"
              inputType="select"
              options={timeOfDayOptions}
              isHorizontal
            />
            <SimpleLabeledPopoverInput isHorizontal name="customer_ref" label="Customer Ref #" />
          </BookingSection>
          <BookingSection title="Details">
            <SimpleLabeledPopoverInput
              isHorizontal
              name="booking_instructions"
              label="Booking details"
              inputType="textarea"
            />
            <SimpleLabeledPopoverInput
              isHorizontal
              name="additional_information"
              label="Additional details"
              inputType="textarea"
            />
          </BookingSection>
        </Stack>
        <Stack flex={1} direction="column" spacing={6}>
          <BookingSection title="Drop-Off">
            <SimpleLabeledPopoverInput
              name="earliest_tender"
              label="Earliest Tender"
              inputType="select"
              isHorizontal
              options={earliestTenderOptions}
            />
            <SimpleLabeledPopoverInput
              name="cutoff_at"
              label="Cut off at"
              inputType="date-picker"
              initialTimeZone={(quote?.origin_airport?.time_zone as TimeZone) || (originTimeZone as TimeZone)}
              isHorizontal
              showTime
              showClearButton
            />
            <SimpleLabeledPopoverInput
              inputType="textarea"
              name="dropoff_instructions"
              label="Drop-Off details"
              isHorizontal
            />
            <SimpleLabeledPopoverAddressInput name="delivery_address" />
            <Button
              variant="ghost"
              size="xs"
              colorScheme="blue"
              leftIcon={<HiOutlineClipboardCopy />}
              onClick={handleClick}
              alignSelf="flex-start"
            >
              Copy Address
            </Button>
            <Stack mt={2}>
              <ContactInput
                name="dropoff_contact_id"
                label="Drop-Off Contact"
                defaultAirlineIds={[quote?.provider_id]}
                defaultAirportIds={[quote?.origin_airport_id]}
                my={1}
                mx={2}
              />
              <LabeledText
                label="Email"
                textProps={{ noOfLines: 1 }}
                value={dropoff_contact?.email || '-'}
                isCopyable
                isHorizontal
                py={1}
                px={2}
              />
              <LabeledText
                label="Phone"
                textProps={{ noOfLines: 1 }}
                value={dropoff_contact?.phone_number || '-'}
                isHorizontal
                py={1}
                px={2}
              />
              <ContactInput
                flex={1}
                name="emergency_contact_id"
                label="Emergency Contact"
                defaultAccountIds={[String(request?.account_id)]}
                my={1}
                mx={2}
              />
            </Stack>
          </BookingSection>
        </Stack>
      </Stack>
    </Stack>
  );
};
